import React, { useState, useEffect } from 'react';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const DEFAULT_FORMAT = `标准商务应标文件格式，包含以下章节：
1. 项目概述与需求理解
2. 解决方案总体架构
3. 产品功能与技术优势
4. 针对需求逐条响应
5. 实施方案与服务保障
6. 项目总结`;

export default function BidDocumentBuilder() {
  const [user, setUser] = useState('');
  const [products, setProducts] = useState([]);
  const [requirement, setRequirement] = useState('');
  const [productIds, setProductIds] = useState([]);
  const [format, setFormat] = useState(DEFAULT_FORMAT);
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setUser(localStorage.getItem('notes_user') || '');
    fetch(`${API_URL}/api/bid/products`)
      .then(r => r.json())
      .then(d => { if (d.success) setProducts(d.products); })
      .catch(() => {});
  }, []);

  if (user !== 'jch') {
    return (
      <div className="bid-container">
        <div className="bid-no-permission">
          <span className="no-permission-icon">🔒</span>
          <h2>无权限访问</h2>
          <p>标书编写功能仅限授权账号使用</p>
        </div>
      </div>
    );
  }

  const handleGenerate = async () => {
    if (!requirement.trim()) {
      setError('请填写招采需求');
      return;
    }
    if (!productIds || productIds.length === 0) {
      setError('请选择对应产品');
      return;
    }
    setError('');
    setGenerating(true);
    setContent('');

    const token = localStorage.getItem('notes_token') || '';
    try {
      const resp = await fetch(`${API_URL}/api/bid/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
        body: JSON.stringify({
          requirement: requirement.trim(),
          productIds,
          format: format.trim() || DEFAULT_FORMAT,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setContent(data.content);
      } else {
        setError(data.error || '生成失败');
      }
    } catch (e) {
      setError('请求失败: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    if (!content.trim()) return;
    const token = localStorage.getItem('notes_token') || '';
    const product = products.find(p => p.id === productIds[0]);
    const productLabel = productIds.map(id => products.find(p => p.id === id)?.name).filter(Boolean).join('+');

    try {
      const resp = await fetch(`${API_URL}/api/bid/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
        body: JSON.stringify({
          title: `${productLabel || '应标'}_应标文件`,
          content,
          productIds,
        }),
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${productLabel || '应标'}_应标文件.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const err = await resp.json();
        setError(err.error || '导出失败');
      }
    } catch (e) {
      setError('导出失败: ' + e.message);
    }
  };

  return (
    <div className="bid-container">
      <div className="bid-header">
        <h2>📝 标书编写</h2>
        <span className="bid-subtitle">AI辅助生成应标文件，基于产品知识库智能匹配方案</span>
      </div>

      <div className="bid-body">
        <div className="bid-input-section">
          <div className="bid-field">
            <label>📋 招采需求</label>
            <textarea
              className="bid-textarea"
              placeholder="请输入招采需求描述，例如：&#10;1. 客户需要一套财务管理系统，支持多组织核算&#10;2. 需要预算管理与费用控制&#10;3. 要求支持银企直联&#10;4. 需要移动端审批功能&#10;5. 要求与现有HR系统对接"
              value={requirement}
              onChange={e => setRequirement(e.target.value)}
              rows={10}
            />
          </div>

          <div className="bid-row">
            <div className="bid-field" style={{flex:1}}>
              <label>📦 对应产品 <span className="bid-hint">（可多选）</span></label>
              <div className="bid-product-list">
                {products.map(p => {
                  const checked = productIds.includes(p.id);
                  return (
                    <label key={p.id} className={`bid-product-item ${checked ? 'checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          if (e.target.checked) setProductIds([...productIds, p.id]);
                          else setProductIds(productIds.filter(id => id !== p.id));
                        }}
                      />
                      <span className="bid-product-name">{p.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bid-field">
            <label>📄 格式要求</label>
            <textarea
              className="bid-textarea format-textarea"
              placeholder="输入格式或排版要求，可留空使用默认格式"
              value={format}
              onChange={e => setFormat(e.target.value)}
              rows={6}
            />
          </div>

          {error && <div className="bid-error">{error}</div>}

          <div className="bid-actions">
            <button
              className="btn-bid-generate"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <><span className="loading-spinner" /> 正在AI生成中...</>
              ) : (
                '🚀 生成应标文件'
              )}
            </button>
          </div>
        </div>

        {content && (
          <div className="bid-result-section">
            <div className="bid-result-header">
              <h3>📄 生成结果</h3>
              <button className="btn-bid-export" onClick={handleExport}>
                📥 导出 Word
              </button>
            </div>
            <div className="bid-result-content">
              {content.split('\n').map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return <br key={i} />;
                // 标题行样式
                const isTitle = /^[一二三四五六七八九十]+[、.．]/.test(trimmed) ||
                               /^\d+[、.．\s]/.test(trimmed);
                if (isTitle) {
                  return <h4 key={i} className="bid-result-title">{trimmed}</h4>;
                }
                return <p key={i} className="bid-result-para">{trimmed}</p>;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
